import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '~in-app/lib/utils'

const iconButtonVariants = tv({
  base: 'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50',
  variants: {
    color: {
      primary: 'bg-primary/90 text-primary-foreground hover:bg-primary',
      secondary: 'bg-secondary/90 text-secondary-foreground hover:bg-secondary',
      error: 'bg-error/90 text-error-foreground hover:bg-error',
      warning: 'bg-warning/90 text-warning-foreground hover:bg-warning',
      info: 'bg-info/90 text-info-foreground hover:bg-info',
      success: 'bg-success/90 text-success-foreground hover:bg-success',
      muted: 'bg-muted/90 text-muted-foreground hover:bg-muted',
    },
    shape: {
      circle: 'rounded-full',
    },
    size: {
      small: 'size-8 [&>svg]:text-xl',
      medium: 'size-10 [&>svg]:text-2xl',
      large: 'size-12 [&>svg]:text-3xl',
    },
    variant: {
      text: 'bg-transparent hover:bg-opacity-20',
      contained: '',
      outlined: 'border border-current bg-transparent hover:bg-opacity-20',
    },
  },
  compoundVariants: [
    {
      color: 'primary',
      variant: 'text',
      class: 'text-primary hover:bg-primary-300',
    },
    {
      color: 'secondary',
      variant: 'text',
      class: 'text-secondary hover:bg-secondary-300',
    },
    {
      color: 'error',
      variant: 'text',
      class: 'text-error hover:bg-error-300',
    },
    {
      color: 'warning',
      variant: 'text',
      class: 'text-warning hover:bg-warning-300',
    },
    {
      color: 'info',
      variant: 'text',
      class: 'text-info hover:bg-info-300',
    },
    {
      color: 'success',
      variant: 'text',
      class: 'text-success hover:bg-success-300',
    },
  ],
  defaultVariants: {
    variant: 'contained',
    color: 'primary',
    size: 'small',
  },
})

export type IconButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof iconButtonVariants> & {
    asChild?: boolean
  }

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, color, variant, size, shape, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        ref={ref}
        className={cn(iconButtonVariants({ variant, color, size, shape, className }))}
        {...props}
      />
    )
  },
)
IconButton.displayName = 'IconButton'

export { IconButton, iconButtonVariants }
